import appStore from '@/stores';
import AppController from "@/api/seller/Controller/AppController";
import { TrackEvent, EventListener } from './monitoringItem/baseListener';
import InputStayListener from './monitoringItem/inputStay';
import FieldErrorListener from './monitoringItem/fieldError';
import ButtonClickListener from './monitoringItem/buttonClick';
import ApiRequestListener from './monitoringItem/apiRequest';
import queryString from 'query-string';
import { getuuid } from '@/common/functions';
import { isEmpty, isEqual } from 'lodash-es';

export type TrackerConfig = {
  reportUrl?: string;
  reportNumberOnce?: number;
  listeners?: Array<any>;
  ext?: any;
  extUtils?: Array<any>;
}

export interface TrackerData {
  category: string, //分析的指标类型，例如：用户行为'userBehavior'
  traceId: string, //轨迹ID
  pathName: string, //页面路径
  ext?:any, //自定义扩展数据
  startTime:number,//开始时间戳
  endTime: number,//结束时间戳
  userId?: string,
  customerCode?: string,
  customerName?: string,
  href?: string,
}
type ReportParams = TrackerData & {
  nodes: TrackEvent[],
}

class Tracker {
  private container: HTMLElement;
  private otherConfig: any;
  private listeners: EventListener[] = [];
  private reportUrl: string;
  private reportNumberOnce: number;
  private flushIndex: number = 0;
  private failedReportedData: any[] = [];
  private eventQueue: TrackEvent[] = [];
  private trackerBaseData: TrackerData = {
    category:'',
    traceId: '',
    pathName: '',
    ext: {},
    startTime: 0,
    endTime: 0,
    userId: '',
    customerCode: '',
    customerName: '',
  };

  constructor(container: HTMLElement, config?: TrackerConfig) {
    this.container = container;
    this.otherConfig = config;
    this.reportUrl = config?.reportUrl ?? 'ulog.v1.add.uba';
    this.reportNumberOnce = config?.reportNumberOnce ?? 50;
    this.createTrackerBaseData();
    this.initListeners();
  }
  private createTrackerBaseData() {
    this.trackerBaseData = {
      category:'userBehavior',
      traceId: getuuid('userBehavior'),
      pathName: window.location.pathname,
      ext: {
        ...queryString.parse(window.location.search),
        ...(this.otherConfig?.ext || {})
      },
      startTime: 0,
      endTime: 0,
      userId: appStore.config?.userId,
      customerCode: appStore.config?.customerCode,
      customerName: appStore.config?.customerName,
      href: window.location.href,
    };
  }
  private initListeners() {
    this.listeners = [
      new InputStayListener(),
      new FieldErrorListener(),
      new ButtonClickListener(),
      new ApiRequestListener(),
      ...(this.otherConfig?.listeners??[]),
    ];
    const onEmit = this.handleEvent.bind(this);
    this.listeners.forEach(listener => listener.start(onEmit,this.container,this.trackerBaseData));
  }

  private handleEvent(event: TrackEvent, el?: HTMLElement) {
    event.ext = {
      lang: appStore.locale, //每条操作记录带上语言环境
      ...(event.ext || {}),
      ...(this.getModalName(el,event.nodeType) || {}),//弹窗内的节点操作记录弹窗名字
      ...(this.otherConfig?.extUtils || [])
        .reduce((exts: Record<string, any>, util: any) => ({ ...exts, ...util.getExt() }), {})
    };
    let lastEvent = this.eventQueue[this.eventQueue.length - 1];
    if ((event.nodeType === 'api' && event.url === this.reportUrl)
      || (event.nodeType === 'api' && !this.checkVisibility())
      || this.eventIsEqual(lastEvent,event)
    ) {
      return;
    }
    if (this.eventQueue.length === 0) {
      this.trackerBaseData.startTime = event.timestamp;
    }
    //接口记录和弹窗报错记录连续且间隔100ms内, 默认是接口报错，将报错信息合并到接口请求记录
    if (event.nodeType === 'errorModal'
      && lastEvent
      && lastEvent.nodeType === 'api'
      && event.timestamp - lastEvent.timestamp < 100) {
      let {nodeType, ...data} = event;
      lastEvent = {...lastEvent, ...data};
      this.eventQueue[this.eventQueue.length - 1] = lastEvent;
    } else {
      this.eventQueue.push(event);
    }
    // console.log('操作事件：',{
    //   ...this.trackerBaseData,
    //   endTime: Date.now(),
    //   nodes: this.eventQueue,
    // });
    this.reportingData();
  }
  //重新初始化
  public reset() {
    this.destroy();
    this.createTrackerBaseData();
    this.initListeners();
  }
  destroy() {
    //文件下载window.location.href=xxx方式，会触发导致销毁监听
    // this.listeners.forEach(listener => listener.stop());
    // this.listeners = [];
    this.reportingDataLast();
  }
  //事件上报
  private async reportingData() {
    const _eventQueue = this.eventQueue.slice(this.flushIndex);
    if (_eventQueue?.length < this.reportNumberOnce) {
      return;
    }
    this.flushIndex = this.eventQueue.length;
    const params = {
      ...this.trackerBaseData,
      endTime: Date.now(),
      nodes: _eventQueue,
    };
    // console.log('上报数据：', params);
    const res = await AppController.ajaxProcess({
      api: this.reportUrl,
      form: params
    }, { throwError: false });
    if (res.status !== 1) {
      this.failedReportedData.push(params);
    }
  }
  public async reportingDataLast() {
    let _failedReportedData = [...this.failedReportedData];
    if (_failedReportedData?.length > 0) {
      this.failedReportedData = [];
      this.failedReportedData.forEach(async (params) => {
        this.sendBeaconData(params);
      });
    }
    const _eventQueue = this.eventQueue.slice(this.flushIndex);
    if (_eventQueue?.length) {
      let params:any ={
        ...this.trackerBaseData,
        endTime: Date.now(),
        nodes: _eventQueue,
      };
      this.sendBeaconData(params);
    }
  }
  private async sendBeaconData(params: ReportParams) {
    const formData = new FormData();
    if(isEmpty(params?.ext)){
      delete params.ext;
    }
    formData.append('api', this.reportUrl);
    formData.append('form', JSON.stringify(params));
    formData.append('jsondata', 'Y');
    // console.log('最后上报数据：', params);
    navigator.sendBeacon(`${appStore.config.url.APP}/ajaxProcess`, formData);
    this.eventQueue = [];
    this.flushIndex = 0;
  }
  private getModalName(el?: HTMLElement, type?: string) {
    let modalName, modalKey, modalExt;
    let modalEl = el?.closest('.winitd-modal');
    if (type === 'api' && !el) {
      // 获取第一个显示的弹窗元素
      modalEl = Array.from(this.container?.querySelectorAll?.('.winitd-modal')).filter((ele) => {
        const htmlElement = ele as HTMLElement;
        const style = window.getComputedStyle(htmlElement);
        return style.display !== 'none' &&
              style.visibility !== 'hidden' &&
              style.opacity !== '0' &&
              htmlElement.offsetWidth > 0 &&
              htmlElement.offsetHeight > 0;
      })?.[0] as HTMLElement;
    }
    if(!modalEl) return null;
    modalName = (modalEl?.querySelector('.winitd-modal-title') as HTMLElement)?.innerText?.trim();
    modalKey = modalEl.closest('.winitd-modal-root')?.getAttribute('data-modal-key')?.trim();
    try {
      modalExt = JSON.parse(modalEl.closest('.winitd-modal-root')?.getAttribute('data-monitor-ext') || '{}');
    } catch (e) {
      console.error(`解析自定义扩展数据失败:`, e);
    }
    if (!modalName && !modalKey) return null;
    return {
      modalName,
      modalKey,
      ...isEmpty(modalExt) ? {} : modalExt,
    };
  }
  private checkVisibility() {
    if (typeof (this.container as any)?.checkVisibility === 'function') {
      return (this.container as any)?.checkVisibility();
    } else {
      return this.container?.offsetWidth > 0 && this.container?.offsetHeight > 0;
    }
  }
  //判断两个事件是否一样，超短时间内一样的事件去重
  private eventIsEqual(prevEvent:any, curEvent:any) {
    if (!prevEvent || !curEvent) return false;
    let { timestamp:prevt, ...preve } = prevEvent;
    let { timestamp:curt, ...cure } = curEvent;
    return Number(curt)-Number(prevt)<10 && isEqual(preve, cure);
  }
}
export default Tracker;
